import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Rocket, 
  Mail, 
  Search, 
  Zap, 
  BarChart, 
  Calendar, 
  Clock, 
  CheckCircle, 
  X, 
  ArrowRight, 
  Linkedin 
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';
import SubmissionPopup from '../components/SubmissionPopup';

export default function PhilipMcParlane() {
  const [showPopup, setShowPopup] = useState(false);
  const [waitUntilMonday, setWaitUntilMonday] = useState(false);

  const handleGetStartedNow = () => {
    window.location.href = 'https://cal.com/dsmarketing.meeting.with.pascoal/philip-pascoal-setup-campaign-call';
  };

  const handleWaitUntilMonday = () => {
    setWaitUntilMonday(true);
    setShowPopup(true);
  };

  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Rocket className="w-16 h-16 text-golden animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Hi <span className="text-golden">Philip</span>!
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            It was great talking with you today about CRM Inputs' lead generation needs
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              As promised, I've put together this detailed breakdown of our automated lead generation system that we discussed. This personalized approach will help CRM Inputs connect with high-quality prospects at scale.
            </p>
          </div>
        </motion.div>

        {/* Introduction Section */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">The Automated Lead Generation System</h2>
          <p className="text-gray-300 mb-6">
            Our system leverages cutting-edge automation to deliver massive personalized outreach campaigns, sending up to <span className="text-golden font-semibold">2,000 highly targeted emails daily</span>. This isn't just about volume—it's about creating meaningful connections with potential clients who need CRM Inputs' services.
          </p>
          <p className="text-gray-300 mb-6">
            What makes this approach different is the level of personalization. We don't just send emails to random contacts—we specifically reach out to companies that are actively recruiting, ensuring our outreach is relevant and timely. This results in significantly higher engagement rates compared to traditional mass email campaigns.
          </p>
          <p className="text-gray-300">
            The system works 24/7, continuously generating leads while you focus on your core business activities. It's like having an entire sales development team working around the clock, but at a fraction of the cost.
          </p>
        </MouseFollowEffect>

        {/* System Components */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-8 text-center">System Components</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Linkedin className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">LinkedIn Sales Navigator</h3>
                  <p className="text-gray-300">
                    We use LinkedIn Sales Navigator to identify and target the perfect prospects for CRM Inputs. This powerful tool allows us to filter by industry, company size, job title, and more, ensuring we're reaching decision-makers who are most likely to need your services.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Zap className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Make.com</h3>
                  <p className="text-gray-300">
                    Make.com serves as the automation backbone of our system, connecting all components and orchestrating the entire workflow. It handles everything from data processing to campaign management, ensuring seamless operation without manual intervention.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Mail className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Instantly.ai</h3>
                  <p className="text-gray-300">
                    Instantly.ai handles the email delivery with industry-leading deliverability rates. It manages email warming, sending schedules, and follow-ups automatically. The platform's advanced analytics also provide real-time insights into campaign performance.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Search className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Any Mail Finder</h3>
                  <p className="text-gray-300">
                    This specialized tool helps us discover verified email addresses for your target prospects with exceptional accuracy. It ensures we're reaching the right people through their preferred communication channel, significantly increasing response rates.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* System Structure */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">System Structure</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="mb-6">
              <img 
                src="/System 3/Screenshot 2025-02-27 at 16.49.22.png" 
                alt="Lead Generation System Structure" 
                className="w-full rounded-xl"
              />
            </div>
            <h3 className="text-xl font-bold mb-4 text-golden">How It All Works Together</h3>
            <ol className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">1.</span>
                <span><strong>Prospect Identification:</strong> LinkedIn Sales Navigator identifies ideal prospects based on CRM Inputs' target customer profile.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">2.</span>
                <span><strong>Data Enrichment:</strong> Any Mail Finder discovers and verifies email addresses for these prospects.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">3.</span>
                <span><strong>Automation Orchestration:</strong> Make.com connects all tools and manages the workflow between platforms.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">4.</span>
                <span><strong>Personalized Outreach:</strong> Instantly.ai delivers highly personalized emails with automatic follow-ups.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">5.</span>
                <span><strong>Response Management:</strong> The system tracks responses and alerts your team when prospects engage.</span>
              </li>
              <li className="flex items-start">
                <span className="text-golden font-bold mr-2">6.</span>
                <span><strong>Continuous Optimization:</strong> We analyze results and refine the approach for ever-improving performance.</span>
              </li>
            </ol>
          </div>
        </div>

        {/* Campaign Metrics */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Proven Results</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4 text-golden">Campaign Metrics</h3>
              <div className="mb-6">
                <img 
                  src="/System 3/Metrics.png" 
                  alt="Campaign Metrics" 
                  className="w-full rounded-xl"
                />
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Open Rate:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">78%</span>
                    <span className="text-green-500 ml-2 text-sm">(+46% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Reply Rate:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">15%</span>
                    <span className="text-green-500 ml-2 text-sm">(+10% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Meeting Conversion:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">4.2%</span>
                    <span className="text-green-500 ml-2 text-sm">(+2.7% vs. industry avg.)</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Cost Per Meeting:</span>
                  <div className="flex items-center">
                    <span className="text-golden font-bold">$38</span>
                    <span className="text-green-500 ml-2 text-sm">(vs. $250+ industry avg.)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <h3 className="text-xl font-bold mb-4 text-golden">What This Means for CRM Inputs</h3>
              <div className="space-y-4 text-gray-300">
                <p>
                  Based on these metrics and your current market position, CRM Inputs could expect:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>40-60 qualified meetings per month</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>4-10 new clients per month (based on your current conversion rate)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Estimated ROI of 250-350% within 90 days</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Significant brand awareness boost in your target market</span>
                  </li>
                </ul>
                <p className="mt-4">
                  These results would transform CRM Inputs' growth trajectory, creating a predictable and scalable lead generation engine.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Icebreaker Example */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">The Power of Personalization</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Sample Icebreaker</h3>
                <div className="mb-6">
                  <img 
                    src="/System 3/Icebreaker.png" 
                    alt="Icebreaker Example" 
                    className="w-full rounded-xl"
                  />
                </div>
                <p className="text-gray-300 italic">
                  "If you were Natalia, you'd actually reply to this email."
                </p>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Why This Works</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Personalized with specific details about the recipient</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Conversational and authentic tone (not salesy)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Clear value proposition tailored to their needs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Specific, relevant social proof</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Low-pressure call to action</span>
                  </li>
                </ul>
                <p className="mt-4 text-gray-300">
                  For CRM Inputs, we'll craft similar personalized messages that speak directly to the challenges your prospects face with data management and CRM implementation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Implementation Roadmap */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Implementation Roadmap</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">1-2 Week Setup</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <Clock className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Week 1:</strong> System configuration, email account setup, and target audience definition</span>
                  </li>
                  <li className="flex items-start">
                    <Clock className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Week 2:</strong> Message crafting, campaign structure, and initial testing</span>
                  </li>
                </ul>
                <div className="mt-6">
                  <h4 className="font-bold text-golden mb-2">Required Resources</h4>
                  <p className="text-gray-300">
                    Total implementation cost: <span className="text-golden font-bold">$2,300.00</span>
                  </p>
                  <p className="text-gray-300 text-sm mt-2">
                    This covers all setup costs, platform fees, and implementation services.
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Platform Stack</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>LinkedIn Sales Navigator:</strong> Prospect identification and targeting</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Make.com:</strong> Workflow automation and system integration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Instantly.ai:</strong> Email delivery and campaign management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Any Mail Finder:</strong> Email discovery and verification</span>
                  </li>
                </ul>
                <div className="mt-6">
                  <h4 className="font-bold text-golden mb-2">Expected ROI</h4>
                  <p className="text-gray-300">
                    Within 30 days, you can expect a positive return on investment, potentially generating <span className="text-golden font-bold">2.5x</span> the initial investment.
                  </p>
                  <p className="text-gray-300 text-sm mt-2">
                    Based on your current customer lifetime value, this system could deliver $5,750+ in value within the first month.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-center">Ready to Transform CRM Inputs' Lead Generation?</h2>
            <p className="text-xl text-gray-300 mb-8 text-center">
              Philip, I'll be waiting for your email on Monday morning to get started. However, the sooner we begin, the sooner you'll see results.
            </p>
            <p className="text-gray-300 mb-8 text-center">
              <strong className="text-golden">Don't miss out:</strong> Every day you wait is a day your competitors might be reaching your potential clients.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetStartedNow}
                className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 flex items-center justify-center"
              >
                Philip, Let's Get Started Now <ArrowRight className="ml-2 w-4 h-4" />
              </button>
              <button
                onClick={handleWaitUntilMonday}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-full font-semibold hover:border-gray-400 hover:text-white transition-all duration-300"
              >
                I'll Wait Until Monday
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <SubmissionPopup 
        isOpen={showPopup} 
        onClose={() => setShowPopup(false)} 
        message={waitUntilMonday ? "Thanks Philip! I'll be waiting for your email on Monday morning." : ""}
      />
    </div>
  );
}